import React, { useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Alert,
    Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Email, Home, QrCode, OpenInNew } from '@mui/icons-material';
import { netflixAccountApi } from '../../services/api';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import '../../i18n';

const MainInterface: React.FC = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [emailError, setEmailError] = useState('');
    const [travelCodeLink, setTravelCodeLink] = useState<string | null>(null);
    const [householdLink, setHouseholdLink] = useState<string | null>(null);
    const { t } = useTranslation();

    const decodeHtmlEntities = (str: string): string => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        return textarea.value;
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setEmailError('');
        setError('');
        setSuccess('');
        setTravelCodeLink(null);
        setHouseholdLink(null);
    };

    const handleAction = async (actionType: 'travel' | 'household') => {
        if (!email) {
            setEmailError(t('mainInterface.emailRequired'));
            return;
        }

        if (!validateEmail(email)) {
            setEmailError(t('mainInterface.invalidEmail'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setTravelCodeLink(null);
        setHouseholdLink(null);

        try {
            const response = await netflixAccountApi.checkAccount(email);
            
            if (!response.exists) {
                setError(t('mainInterface.noAccountFound'));
                return;
            }

            const emailCheck = actionType === 'travel' 
                ? await netflixAccountApi.checkTravelCode(email)
                : await netflixAccountApi.checkHousehold(email);

            if (emailCheck.found && emailCheck.link) {
                const decodedLink = decodeHtmlEntities(emailCheck.link);
                if (actionType === 'travel') {
                    setTravelCodeLink(decodedLink);
                } else {
                    setHouseholdLink(decodedLink);
                }
                setSuccess(actionType === 'travel' 
                    ? t('mainInterface.travelCodeFound')
                    : t('mainInterface.householdUpdateFound')
                );
            } else {
                setError(t('mainInterface.noRecentRequest'));
            }

        } catch (err) {
            setError(t('netflix.checkError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCheckTravelCode = async () => {
        if (!email) {
            setEmailError(t('mainInterface.emailRequired'));
            return;
        }

        if (!validateEmail(email)) {
            setEmailError(t('mainInterface.invalidEmail'));
            return;
        }

        setLoading(true);
        setError('');
        try {
            const accountCheck = await netflixAccountApi.checkAccount(email);
            
            if (!accountCheck.exists) {
                setError(t('mainInterface.noAccountFound'));
                return;
            }

            const result = await netflixAccountApi.checkTravelCode(email);
            if (result.found && result.link) {
                setTravelCodeLink(result.link);
            } else {
                setError(t('mainInterface.noTravelCodeRequest'));
            }
        } catch (err) {
            setError(t('netflix.checkError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCheckHousehold = async () => {
        if (!email) {
            setEmailError(t('mainInterface.emailRequired'));
            return;
        }

        if (!validateEmail(email)) {
            setEmailError(t('mainInterface.invalidEmail'));
            return;
        }

        setLoading(true);
        setError('');
        try {
            const accountCheck = await netflixAccountApi.checkAccount(email);
            
            if (!accountCheck.exists) {
                setError(t('mainInterface.noAccountFound'));
                return;
            }

            const result = await netflixAccountApi.checkHousehold(email);
            if (result.found && result.link) {
                setHouseholdLink(result.link);
            } else {
                setError(t('mainInterface.noHouseholdRequest'));
            }
        } catch (err) {
            setError(t('netflix.checkError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
                <LanguageSwitcher />
            </Box>
            <Paper elevation={3} sx={{ mt: 2, p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom color="primary">
                        {t('mainInterface.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('mainInterface.subtitle')}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        {success}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label={t('mainInterface.email')}
                    variant="outlined"
                    value={email}
                    onChange={handleEmailChange}
                    error={!!emailError}
                    helperText={emailError}
                    InputProps={{
                        startAdornment: <Email color="action" sx={{ mr: 1 }} />,
                    }}
                    sx={{ mb: 3 }}
                />

                <Stack spacing={2}>
                    {travelCodeLink && (
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            startIcon={<OpenInNew />}
                            href={travelCodeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('mainInterface.openNetflixLink')}
                        </Button>
                    )}

                    {householdLink && (
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            startIcon={<OpenInNew />}
                            href={householdLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('mainInterface.openNetflixLink')}
                        </Button>
                    )}

                    {!travelCodeLink && !householdLink && (
                        <>
                            <LoadingButton
                                fullWidth
                                variant="contained"
                                size="large"
                                loading={loading}
                                startIcon={<QrCode />}
                                onClick={handleCheckTravelCode}
                                disabled={!email || !!emailError}
                            >
                                {t('mainInterface.checkTravelCode')}
                            </LoadingButton>

                            <LoadingButton
                                fullWidth
                                variant="outlined"
                                size="large"
                                loading={loading}
                                startIcon={<Home />}
                                onClick={handleCheckHousehold}
                                disabled={!email || !!emailError}
                            >
                                {t('mainInterface.checkHousehold')}
                            </LoadingButton>
                        </>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
};

export default MainInterface; 